import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Login from './components/auth/Login';
import Layout from './components/common/Layout';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';

// Import your pages
import Dashboard from './components/pages/Dashboard';
import Interventions from './components/pages/Interventions';
import Clients from './components/pages/Clients/index.jsx';
import Articles from './components/pages/Articles/index.jsx';
import FamilleTypeArticle from './components/pages/Articles/FamilleTypeArticle';
import TypeArticle from './components/pages/Articles/TypeArticle';
import PiecesRechange from './components/pages/PiecesRechange';
import Services from './components/pages/Services';
import Certificats from './components/pages/Certificats';
import Locaux from './components/pages/Locaux';
import EntretienPage from './components/pages/EntretienPage';


import { useAuth } from './contexts/AuthContext';

function App() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Map page keys to routes
  const pageRoutes = {
    dashboard: '/',
    interventions: '/interventions',
    clients: '/clients',
    articles: '/articles',
    'famille-type-article': '/famille-type-article',
    'type-article': '/type-article',
    'pieces-rechange': '/pieces-rechange',
    services: '/services',
    certificats: '/certificats',
    locaux: '/locaux',
    entretien: '/entretien',
  };

  // Helper: admin-only route wrapper
  const NotAuthorized = () => (
    <div style={{ padding: 40, textAlign: 'center', color: 'red', fontWeight: 'bold' }}>
      Accès non autorisé
    </div>
  );

  // Map routes to components, with admin protection
  const routeComponents = {
    '/': <Dashboard />,
    '/interventions': <Interventions />,
    '/clients': user?.isAdmin ? <Clients /> : <NotAuthorized />,
    '/articles': <Articles />,
    '/famille-type-article': user?.isAdmin ? <FamilleTypeArticle /> : <NotAuthorized />,
    '/type-article': user?.isAdmin ? <TypeArticle /> : <NotAuthorized />,
    '/pieces-rechange': user?.isAdmin ? <PiecesRechange /> : <NotAuthorized />,
    '/services': <Services />,
    '/certificats': <Certificats />,
    '/locaux': <Locaux />,
    '/entretien': user?.isAdmin
      ? (() => {
          let interventionData = null;
          let interventionId = null;
          try {
            const params = new URLSearchParams(window.location.search);
            interventionId = params.get('id');
            const stored = localStorage.getItem('entretienIntervention');
            if (stored) {
              const parsed = JSON.parse(stored);
              if (interventionId && parsed && String(parsed.id) === String(interventionId)) {
                // Check if intervention is closed
                if (parsed.cloture) {
                  // Redirect back to interventions with an error message
                  setTimeout(() => {
                    alert('Impossible d\'accéder à l\'entretien : cette intervention est clôturée.');
                    window.location.href = '/interventions';
                  }, 100);
                  return <div className="flex items-center justify-center min-h-screen">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
                      <p>Redirection en cours...</p>
                    </div>
                  </div>;
                }
                interventionData = parsed;
              }
            }
          } catch {}
          return <EntretienPage intervention={interventionData} />;
        })()
      : <NotAuthorized />,
  };

  // Sidebar navigation handler
  const setCurrentPage = (pageKey) => {
    const url = pageRoutes[pageKey] || '/';
    navigate(url);
  };

  if (!user) {
    return (
      <ThemeProvider>
        <Login onLoginSuccess={() => window.location.reload()} />
      </ThemeProvider>
    );
  }

  // Find the current route (default to dashboard)
  // For /entretien, protect even if accessed with query params
  let currentRoute = Object.values(pageRoutes).includes(location.pathname)
    ? location.pathname
    : '/';

  // Special protection for /entretien (with or without query params)
  if (location.pathname === '/entretien' && !user?.isAdmin) {
    return (
      <ThemeProvider>
        <Layout
          currentPage={Object.keys(pageRoutes).find((k) => pageRoutes[k] === '/entretien') || 'dashboard'}
          setCurrentPage={setCurrentPage}
          userInfo={user}
          onLogout={() => { localStorage.removeItem('userInfo'); localStorage.removeItem('authToken'); window.location.reload(); }}
        >
          <div style={{ padding: 40, textAlign: 'center', color: 'red', fontWeight: 'bold' }}>
            Accès non autorisé
          </div>
        </Layout>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <Layout
        currentPage={Object.keys(pageRoutes).find((k) => pageRoutes[k] === currentRoute) || 'dashboard'}
        setCurrentPage={setCurrentPage}
        userInfo={user}
        onLogout={() => { localStorage.removeItem('userInfo'); localStorage.removeItem('authToken'); window.location.reload(); }}
      >
        {routeComponents[currentRoute] || <Dashboard />}
      </Layout>
    </ThemeProvider>
  );
}


export default function AppWithProvider() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="*" element={<App />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}